import { createApp } from 'vue'
import { createPinia, storeToRefs } from 'pinia'
import { useMessageStack } from '@/stores/messagestack'
import { useModalStack } from '@/stores/modalstack'
import { useUserStore } from '@/stores/user'
import { useUiStore } from '@/stores/ui'
import {useLockStore} from '@/stores/lock'
import { ApiError, setDefaultConfig } from 'soykajs/composables/api'
import { resolveApiErrorMessage } from '@/lib/apiErrors'

import App from '@/app.vue'
import { i18n } from '@/i18n'
import router from '@/router'
import ErrorConnectionModal from '@/components/errorConnectionModal.vue'

import config from '@/config'

import VueFinder from 'vuefinder'
import vueFinderEn from 'vuefinder/dist/locales/en.js'
import vueFinderRu from 'vuefinder/dist/locales/ru.js'
import { startCustomSelects, closeAllCustomSelects } from '@/composables/useCustomSelects'

import { setEditableChecker } from 'soykajs/components/docviewer'
import { initPageZoomLimit } from '@/lib/pageZoom'

setEditableChecker((upload) => {
  if (upload?.editable === false) {
     return false
   }
   if (upload?.metadata?.r7_editable === false) {
     return false
   }
   if (upload?.division === 'service_note') {
     return false
   }
   // Форма согласования подачи — только просмотр / факсимиле, не R7
   if (upload?.division === 'submissions.agreement_form') {
     return false
   }
   return Boolean(upload?.dockey)
})

const app = createApp(App)

app.use(createPinia())
app.use(i18n)
app.use(router)
app.use(VueFinder, {
  i18n: {
    en: vueFinderEn,
    ru: vueFinderRu,
  },
  locale: i18n.global.locale.value,
})

setDefaultConfig({
  url_backend: config.VITE_API_URL,
  url_catalog: config.VITE_CATALOGS_URL,
  url_docparse: config.VITE_DOCPARSER_URL,
  url_sso: config.VITE_SSO_URL,
  url_r7: config.VITE_R7_URL || 'https://r7.dev.atd.systems',
  store_user: useUserStore,
  store_lock: useLockStore,
  service: config.VITE_SERVICE_SELF || 'ltms',
  resolveErrorMessage: resolveApiErrorMessage,
  // X-Locale в каждом запросе: backend рендерит тексты на языке переключателя
  locale: () => i18n.global.locale.value,
})

const messageStack = useMessageStack()
const modalStack = useModalStack()
const { user } = storeToRefs(useUserStore())
const { blockScreen } = storeToRefs(useUiStore())

app.config.errorHandler = errorHandler
app.mount('#app')
initPageZoomLimit()
startCustomSelects(document.getElementById('app') || document.body)
router.beforeEach(() => {
  closeAllCustomSelects()
  // history.back() просмотрщика. Класс ещё стоит: этот переход не закрывает стек модалок.
  const docviewerOverModal =
    document.body.classList.contains('nested-docviewer-active') ||
    document.body.classList.contains('history-docviewer-active')
  if (docviewerOverModal) {
    return
  }
  if (modalStack.stack.length > 0) {
    modalStack.closeAll()
    window.dispatchEvent(new CustomEvent('modalstack:close'))
  }
})

/**
 * Проверяет, является ли ошибка связанной с загрузкой модулей
 */
function isChunkLoadError(err) {
  const errStr = String(err)
  return (
    errStr.includes('Failed to fetch dynamically imported module') ||
    errStr.includes('Failed to load module script') ||
    errStr.includes('Importing a module script failed') ||
    (err instanceof TypeError && errStr.includes('fetch'))
  )
}

/**
 * Показывает модальное окно с ошибкой связи
 */
function showConnectionErrorModal() {
  // Проверяем, не открыта ли уже модалка
  if (modalStack.stack.length > 0) {
    return
  }

  modalStack.push({
    component: ErrorConnectionModal,
    props: {
      headerText: i18n.global.t('common.loadError'),
      size: 'sm',
      fitContent: true
    }
  })
}

function errorText(err) {
  if (err == null) return ''
  if (typeof err === 'string') return err
  return [err.name, err.message, err.reason, String(err)].filter(Boolean).join(' ')
}

function isBenignPdfCloseError(err) {
  return /getpage|transport destroyed|loading aborted|worker was terminated/i.test(errorText(err))
}

/** Chrome/Safari: RO успел изменить layout в том же кадре — не баг приложения. */
function isBenignResizeObserverError(err) {
  return /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i.test(
    errorText(err)
  )
}

async function errorHandler(err) {
  let errMessage = errorText(err) || String(err ?? '')

  // PDF.js: закрытие просмотрщика обрывает getPage — не тост.
  if (isBenignPdfCloseError(err)) {
    return
  }
  if (isBenignResizeObserverError(err)) {
    return
  }

  // VueFinder уже размонтирован (ушли с /finder / HMR), а async ещё дергает API.
  if (/VueFinder app instance with id/i.test(errMessage)) {
    return
  }

  console.error(err)

  // Обработка ошибок загрузки модулей
  if (isChunkLoadError(err)) {
    blockScreen.value = false
    showConnectionErrorModal()
    return
  }

  if (err instanceof ApiError) {
    if (err.code === 401) {
      user.value = undefined
      errMessage = i18n.global.t('common.sessionExpired')
    } else {
      // message уже резолвнут в useApi (resolveErrorMessage); fallback на status
      const statusKey = `http.status.${err.status}`
      const statusLabel = i18n.global.te(statusKey)
        ? i18n.global.t(statusKey)
        : ''
      errMessage = err.message || (statusLabel ? `${err.status} ${statusLabel}` : String(err.status || ''))
    }
  }

  // legacy: английский statusText (Bad Request, …) → http.errors
  const httpErrors = i18n.global.tm('http.errors')
  if (httpErrors && typeof httpErrors === 'object' && errMessage in httpErrors) {
    errMessage = httpErrors[errMessage]
  }

  blockScreen.value = false
  messageStack.push({ type: 'error', message: errMessage })
}

// Обработчик ошибок роутера
router.onError((err) => {
  console.error('Router error:', err)
  if (isChunkLoadError(err)) {
    blockScreen.value = false
    showConnectionErrorModal()
  }
})

window.onerror = (m, u, l, c, e) => {
  const err = e || m
  if (isBenignResizeObserverError(err) || isBenignPdfCloseError(err)) {
    return true
  }
  errorHandler(err)
}
window.onunhandledrejection = ({ reason }) => errorHandler(reason)
